import { useMemo } from 'react';
import { FormTreeData } from '@/features/submission_forms/types';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { Clarification } from '@/types/clarification';
import { forEachQuestionInRenderOrder } from '@/features/submission_forms/clarificationTargets';

interface SectionClarificationInfo {
  sectionId: string;
  hasClarifications: boolean;
  unresolvedCount: number;
  totalCount: number;
}

interface UseSectionClarificationsReturn {
  sectionClarifications: Map<string, SectionClarificationInfo>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Extract all question slugs from a form tree, grouped by top-level section
 * Includes questions from nested child sections in their parent section
 */
const extractQuestionsBySection = (
  formTree: FormTreeData[]
): Map<string, string[]> => {
  const sectionQuestions = new Map<string, string[]>(
    formTree.map(section => [section.id, []])
  );
  forEachQuestionInRenderOrder(formTree, (question, topLevelSection) => {
    sectionQuestions.get(topLevelSection.id)?.push(question.slug);
  });
  return sectionQuestions;
};

/**
 * Hook to compute section-level clarification info from batch context data.
 * No API calls are made here - all data comes from ClarificationsContext.
 */
export const useSectionClarifications = (
  formTree: FormTreeData[]
): UseSectionClarificationsReturn => {
  const { clarificationsByQuestion, isLoading, error, refetch } =
    useClarificationsContext();

  const sectionClarifications = useMemo(() => {
    const sectionMap = new Map<string, SectionClarificationInfo>();

    if (!formTree || formTree.length === 0) {
      return sectionMap;
    }

    const sectionQuestions = extractQuestionsBySection(formTree);

    sectionQuestions.forEach((questionSlugs, sectionId) => {
      let totalCount = 0;
      let unresolvedCount = 0;

      questionSlugs.forEach(questionSlug => {
        const clarifications: Clarification[] =
          clarificationsByQuestion[questionSlug] || [];

        // Count root clarifications only (exclude replies) to stay
        // consistent with unresolvedCount and the context-level total.
        const questionTotal = clarifications.length;
        const questionUnresolved = clarifications.filter(
          c => !c.resolved
        ).length;

        totalCount += questionTotal;
        unresolvedCount += questionUnresolved;
      });

      if (totalCount > 0) {
        sectionMap.set(sectionId, {
          sectionId,
          hasClarifications: true,
          unresolvedCount,
          totalCount,
        });
      }
    });

    return sectionMap;
  }, [formTree, clarificationsByQuestion]);

  return {
    sectionClarifications,
    isLoading,
    error,
    refetch,
  };
};
